import React, { useState, useEffect } from 'react';
import InvoiceDetailModal from '../modals/InvoiceDetailModal';
import GenerateInvoiceModal from '../modals/GenerateInvoiceModal';
import SettingsPage from './SettingsPage';

const API_URL = process.env.REACT_APP_API_URL || '';

function InvoicingPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showXeroSettings, setShowXeroSettings] = useState(false);
  const [vpnStatus, setVpnStatus] = useState('checking');
  const [vpnRestarting, setVpnRestarting] = useState(false);

  useEffect(() => {
    fetchInvoices();
    checkVpnStatus();
    
    // Poll VPN status every 30 seconds
    const vpnInterval = setInterval(checkVpnStatus, 30000);
    return () => clearInterval(vpnInterval);
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/invoices`);
      if (!res.ok) throw new Error('Failed to fetch invoices');
      const data = await res.json();
      setInvoices(data);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      alert('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceClick = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const handleGenerateSuccess = () => {
    setShowGenerateModal(false);
    fetchInvoices();
  };

  const checkVpnStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vpn/status`);
      const data = await res.json();
      setVpnStatus(data.status);
    } catch (err) {
      console.error('Error checking VPN status:', err);
      setVpnStatus('unknown');
    }
  };

  const handleRestartVpn = async () => {
    if (!window.confirm('Restart the VPN connection to QuickEasy database?')) return;
    
    try {
      setVpnRestarting(true);
      const res = await fetch(`${API_URL}/api/vpn/restart`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        alert(data.message);
        checkVpnStatus();
      } else {
        alert('VPN restart failed: ' + data.message);
      }
    } catch (err) {
      console.error('Error restarting VPN:', err);
      alert('Failed to restart VPN. Please contact support.');
    } finally {
      setVpnRestarting(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Failed to delete invoice');
      alert('Invoice deleted successfully');
      fetchInvoices();
    } catch (err) {
      console.error('Error deleting invoice:', err);
      alert('Failed to delete invoice');
    }
  };

  const formatCurrency = (amount) => {
    return 'R' + parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: { bg: '#fff3cd', color: '#856404', text: 'Draft' },
      SENT_TO_XERO: { bg: '#d1ecf1', color: '#0c5460', text: 'Sent to Xero' },
      APPROVED: { bg: '#d4edda', color: '#155724', text: 'Approved' },
      PAID: { bg: '#d4edda', color: '#155724', text: 'Paid' }
    };
    const style = styles[status] || styles.DRAFT;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        background: style.bg,
        color: style.color
      }}>
        {style.text}
      </span>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', position: 'relative' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: '#1976d2' }}>
            📄 Invoicing
          </h1>
          <p style={{ margin: '8px 0 16px 0', color: '#666', fontSize: '14px' }}>
            Generate and manage invoices from QuickEasy Work Tickets
          </p>
        </div>
        
        {/* User Manual Button - Top Right */}
        <button
          onClick={() => window.open('/docs/manuals/invoicing/current/INVOICING_MANUAL_v1.0.html', '_blank')}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
          }}
        >
          📖 User Manual
        </button>
        
        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{ flex: 1 }}></div>
          
          <button
            onClick={() => setShowGenerateModal(true)}
            style={{
              padding: '14px 32px',
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
            }}
          >
            <span style={{ fontSize: '20px' }}>+</span>
            Generate Invoice
          </button>
          
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center' }}>
            {/* VPN Status Indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: vpnStatus === 'connected' ? '#e8f5e9' : vpnStatus === 'disconnected' ? '#ffebee' : '#fff3e0',
              border: `1px solid ${vpnStatus === 'connected' ? '#4caf50' : vpnStatus === 'disconnected' ? '#f44336' : '#ff9800'}`,
              borderRadius: '8px',
              fontSize: '13px'
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: vpnStatus === 'connected' ? '#4caf50' : vpnStatus === 'disconnected' ? '#f44336' : '#ff9800',
                animation: vpnStatus === 'checking' ? 'pulse 1.5s ease-in-out infinite' : 'none'
              }} />
              <span style={{ fontWeight: 600, color: '#333' }}>
                QE: {vpnStatus === 'connected' ? 'Connected' : vpnStatus === 'disconnected' ? 'Disconnected' : vpnStatus === 'not_applicable' ? 'N/A' : 'Checking...'}
              </span>
              {vpnStatus === 'disconnected' && (
                <button
                  onClick={handleRestartVpn}
                  disabled={vpnRestarting}
                  style={{
                    padding: '4px 12px',
                    background: '#1976d2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: vpnRestarting ? 'not-allowed' : 'pointer',
                    opacity: vpnRestarting ? 0.6 : 1
                  }}
                >
                  {vpnRestarting ? 'Restarting...' : 'Restart'}
                </button>
              )}
            </div>

            <button
              onClick={() => setShowXeroSettings(true)}
              style={{
                padding: '12px 24px',
                background: '#13B5EA',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ⚙️ Xero Settings
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Invoices</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#1976d2' }}>
            {invoices.length}
          </div>
        </div>
        <div style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Draft Invoices</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#ff9800' }}>
            {invoices.filter(inv => inv.status === 'DRAFT').length}
          </div>
        </div>
        <div style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Value</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#4caf50' }}>
            {formatCurrency(invoices.reduce((sum, inv) => sum + parseFloat(inv.total), 0))}
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading invoices...
          </div>
        ) : invoices.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>No invoices yet</div>
            <div style={{ fontSize: '14px' }}>Click "Generate Invoice" to create your first invoice</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#666' }}>
                  Invoice #
                </th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#666' }}>
                  Customer
                </th>
                <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '14px', color: '#666' }}>
                  Total
                </th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#666' }}>
                  Status
                </th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#666' }}>
                  Date
                </th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#666' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr 
                  key={invoice.id}
                  onClick={() => handleInvoiceClick(invoice)}
                  style={{ 
                    borderBottom: '1px solid #e0e0e0',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9f9f9'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                >
                  <td style={{ padding: '16px', fontWeight: 600, color: '#1976d2' }}>
                    {invoice.invoiceNumber}
                  </td>
                  <td style={{ padding: '16px' }}>
                    {invoice.customerName}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrency(invoice.total)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td style={{ padding: '16px', color: '#666', fontSize: '14px' }}>
                    {formatDate(invoice.createdAt)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteInvoice(invoice.id);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#f44336',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showDetailModal && selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedInvoice(null);
          }}
          onRefresh={fetchInvoices}
        />
      )}

      {showGenerateModal && (
        <GenerateInvoiceModal
          onClose={() => setShowGenerateModal(false)}
          onSuccess={handleGenerateSuccess}
        />
      )}

      {/* Xero Settings Modal */}
      {showXeroSettings && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowXeroSettings(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: '32px',
                height: '32px',
                zIndex: 1
              }}
            >
              ×
            </button>
            <SettingsPage />
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoicingPage;
